import { Component, inject, ChangeDetectionStrategy, computed, signal } from '@angular/core';
import { SynDialogComponent, SynButtonComponent, SynIconComponent } from '@synergy-design-system/angular';
import { TranslateModule } from '@ngx-translate/core';
import { DialogService, DialogSeverity } from '@core/services/dialog.service';

/** Maps severity to Synergy color token overrides for syn-button */
const SEVERITY_STYLES: Record<DialogSeverity, string> = {
  danger:
    '--syn-color-primary-600: var(--syn-color-danger-600); --syn-color-primary-700: var(--syn-color-danger-700); --syn-color-primary-500: var(--syn-color-danger-500)',
  warning:
    '--syn-color-primary-600: var(--syn-color-warning-600); --syn-color-primary-700: var(--syn-color-warning-700); --syn-color-primary-500: var(--syn-color-warning-500)',
  success:
    '--syn-color-primary-600: var(--syn-color-success-600); --syn-color-primary-700: var(--syn-color-success-700); --syn-color-primary-500: var(--syn-color-success-500)',
  primary: '',
};

/**
 * DialogHostComponent
 *
 * Persistent global confirm dialog driven by DialogService signals.
 * Place once in app.component.html outside the router-outlet.
 *
 * Usage:
 *   this.dialogService.confirm({
 *     message: 'Are you sure?',
 *     confirmSeverity: 'danger',
 *     confirmIcon: 'delete',
 *     onConfirm: () => this.delete(id),
 *   });
 */
@Component({
  selector: 'app-dialog-host',
  standalone: true,
  imports: [SynDialogComponent, SynButtonComponent, SynIconComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <syn-dialog
      [label]="svc.title()"
      [open]="svc.isOpen()"
      (syn-request-close)="svc.cancel()"
    >
      <p class="dialog-message">{{ svc.message() }}</p>

      @if (svc.copyableValue(); as copyableValue) {
        <div class="copyable-value-panel">
          <code class="copyable-value">{{ copyableValue }}</code>
          <syn-button variant="outline" size="small" (click)="copyValue(copyableValue)">
            <syn-icon slot="prefix" [name]="copied() ? 'check' : 'content_copy'"></syn-icon>
            {{ copied() ? 'Copied' : 'Copy token' }}
          </syn-button>
        </div>
      }

      <div slot="footer" class="flex justify-end gap-2">
        @if (!svc.alertMode()) {
          <syn-button variant="outline" (click)="svc.cancel()"><syn-icon slot="prefix" name="close" aria-hidden="true"></syn-icon>{{ 'action.cancel' | translate }}</syn-button>
        }
        <syn-button variant="filled" [style]="confirmStyle()" (click)="svc.accept()"><syn-icon slot="prefix" [name]="svc.confirmIcon()"  aria-hidden="true"></syn-icon>{{ svc.confirmLabel() || ('action.delete' | translate) }}</syn-button>
      </div>
    </syn-dialog>
  `,
  styles: `
    .dialog-message {
      margin: 0;
    }

    .copyable-value-panel {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--syn-spacing-medium);
      margin-top: var(--syn-spacing-large);
      padding: var(--syn-spacing-large);
      border: var(--syn-border-width-default) solid var(--syn-color-primary-500);
      border-radius: var(--syn-border-radius-medium);
      background: var(--syn-color-primary-50);
    }

    .copyable-value {
      display: block;
      max-width: min(36rem, calc(100vw - 8rem));
      color: var(--syn-color-neutral-900);
      font-size: var(--syn-font-size-small);
      font-weight: var(--syn-font-weight-semibold);
      line-height: var(--syn-line-height-dense);
      overflow-wrap: anywhere;
      word-break: break-all;
      user-select: all;
      white-space: pre-wrap;
    }
  `,
})
export class DialogHostComponent {
  protected readonly svc = inject(DialogService);
  protected readonly copied = signal(false);

  protected readonly confirmStyle = computed(() => SEVERITY_STYLES[this.svc.confirmSeverity()] ?? '');

  protected async copyValue(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }
    this.copied.set(true);
    window.setTimeout(() => this.copied.set(false), 2000);
  }
}
